import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { ChevronLeft, ChevronRight, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  student: "Students",
  teacher: "Teachers",
  parent: "Parents",
  school_admin: "Schools",
};

const ROLE_SUBTITLES = {
  student: "Exam prep, practice books & past questions picked for you",
  teacher: "Teaching guides, lesson resources & subject materials",
  parent: "Bundle packs & multi-subject deals for your child",
  school_admin: "Bulk packages, institutional bundles & school-wide resources",
};

export default function RecommendationsCarousel() {
  const { user, token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    fetchRecommendations();
  }, [user, token]);

  const fetchRecommendations = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/products/recommendations?limit=10`, { headers });
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [products]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 320;
    el.scrollBy({ left: direction === "left" ? -cardWidth * 2 : cardWidth * 2, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary/50" />
      </div>
    );
  }

  if (products.length === 0) return null;

  const role = user?.user_type;
  const label = ROLE_LABELS[role] || "You";
  const subtitle = ROLE_SUBTITLES[role] || "Handpicked products based on your interests";

  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white" data-testid="recommendations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-semibold text-amber-600 uppercase tracking-wider">
                Recommended for {label}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900" data-testid="recommendations-title">
              Picked for {label}
            </h2>
            <p className="text-gray-500 mt-1 text-sm sm:text-base">{subtitle}</p>
          </div>
          <Link
            to="/mock-exams"
            className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors self-start sm:self-auto"
            data-testid="recommendations-view-all"
          >
            View All Products &rarr;
          </Link>
        </div>

        {/* Carousel */}
        <div className="relative group">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all opacity-0 group-hover:opacity-100"
              data-testid="recommendations-scroll-left"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            data-testid="recommendations-carousel"
          >
            {products.map((product) => (
              <div key={product.id} className="flex-shrink-0 w-[280px] sm:w-[300px]">
                <ProductCard product={product} />
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all opacity-0 group-hover:opacity-100"
              data-testid="recommendations-scroll-right"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
